// FilePreview component
import React, { useEffect, useState, useRef } from 'react'
import { saveAs } from 'file-saver'
import { Button } from 'primereact/button'
import { faDownload, faTimes } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import * as docx from 'docx-preview'
import config from '../../../../config/config'
import fetchMethodRequest from '../../../../config/service'

function FilePreview({
  fileBuffer,
  fileType,
  closePreview,
  translatedFilePath,
  selectedLanguage,
  originalFileName,
  onReadyToDownload,
}) {
  const [fileURL, setFileURL] = useState(null)
  const docxContainerRef = useRef(null)
  const [serverBlob, setServerBlob] = useState(null)

  const fetchPdfFromServer = async (serverURL) => {
    try {
      const response = await fetchMethodRequest('GET', serverURL, null, "pdfDownload", null, null)
      if (!response.ok) {
        console.error("PDF fetch failed:", response.status, response.statusText)
        return
      }
      const buffer = await response.arrayBuffer()
      const blob = new Blob([buffer], { type: "application/pdf" })
      setServerBlob(blob)
      const blobURL = URL.createObjectURL(blob)
      setFileURL(blobURL)

      if (typeof onReadyToDownload === "function") {
        let filename = "downloaded_file.pdf"
        if (originalFileName) filename = originalFileName
        if (selectedLanguage) filename = `(${selectedLanguage})${filename}`
        onReadyToDownload({ blob, filename })
      }
    } catch (err) {
      console.error("PDF Fetch Error:", err)
    }
  }

  useEffect(() => {
    let url = null
    let serverObjectUrl = null

    if (translatedFilePath) {
      const serverURL = `${config.imgUrl}${translatedFilePath}`
      fetchPdfFromServer(translatedFilePath)
      return
    }
    if (fileBuffer?.data) {
      const byteArray = new Uint8Array(fileBuffer.data)
      const blob = new Blob([byteArray], { type: fileType })
      url = URL.createObjectURL(blob)
      setFileURL(url)

      if (typeof onReadyToDownload === "function") {
        let filename = `downloaded_file.${fileType === 'application/pdf' ? 'pdf' : 'docx'}`
        if (originalFileName && fileType === 'application/pdf') filename = originalFileName
        if (selectedLanguage) filename = `(${selectedLanguage})${filename}`
        onReadyToDownload({ blob, filename })
      }

      if (fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') {
        (async () => {
          const arrayBuffer = await blob.arrayBuffer()
          await docx.renderAsync(arrayBuffer, docxContainerRef.current, null, {
            style: `
              .docx-container {
                width: 100%;
                max-width: 100%;
                margin: 0 auto;
                transform: scale(0.9);
                transform-origin: top center;
                overflow: hidden;
                padding: 15px;
                background: #fff;
                border-radius: 8px;
              }
              .docx img {
                max-width: 100%;
                height: auto;
              }
              .docx table {
                width: 100%;
                border-collapse: collapse;
                font-size: 0.9em;
              }
              .docx th, .docx td {
                border: 1px solid #dee2e6;
                padding: 8px;
              }
              .docx h1, .docx h2, .docx h3, .docx p {
                margin: 8px 0;
                font-size: 0.95em;
              }
            `,
          })
        })()
      }
    }

    return () => {
      if (url) URL.revokeObjectURL(url)
    }
  }, [fileBuffer, fileType, translatedFilePath])

  const handleDownload = () => {
    if (serverBlob) {
      let filename = "downloaded_file.pdf"
      if (originalFileName) {
        filename = originalFileName
      }
      if (selectedLanguage) {
        filename = `(${selectedLanguage})${filename}`
      }
      saveAs(serverBlob, filename)
      return
    }

    if (!fileBuffer?.data) return

    const byteArray = new Uint8Array(fileBuffer.data)
    const blob = new Blob([byteArray], { type: fileType })
    saveAs(blob, `downloaded_file.${fileType === 'application/pdf' ? 'pdf' : 'docx'}`)
  }

  return (
    <div className="container-fluid h-100">

      <div className="row h-100">
        <div className="col-12 h-100">
          {fileType === 'application/pdf' && fileURL && (
            <div className="h-100 bg-white rounded">
              <iframe
                src={`${fileURL}#toolbar=0`}
                title="PDF Preview"
                className="w-100 h-100 border-0 rounded"
                style={{ minHeight: 'calc(100vh - 200px)' }}
              />
            </div>
          )}

          {fileType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' && (
            <div className="h-100 bg-white rounded overflow-auto p-3 border">
              <div
                ref={docxContainerRef}
                className="docx-container w-100"
              />
            </div>
          )}

          {!fileURL && !fileBuffer?.data && (
            <div className="h-100 d-flex flex-column justify-content-center align-items-center">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="text-muted">Loading preview...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default FilePreview